/**
 * Identity flow (DESIGN-server §6.2) over any bridge:
 *   discord: bridge.authorize() → POST /api/auth/discord { code, guestToken } →
 *            { access_token, token, user } → bridge.authenticate(access_token)
 *   web/mock: GET /api/auth/session (Bearer <stored token> when we have one) → { token, user }
 * The JWT is kept in localStorage `sn.token` (cookies are unreliable inside the iframe; the
 * body token is authoritative). Mock without a reachable server falls back to a local
 * identity with no token, so pure-client specs (ScriptedDriver) still boot.
 *
 * The web path also finishes the two ways a guest saves their progress (docs/accounts/PLAN.md):
 * a mailed `#login=<token>` is redeemed at `POST /api/auth/email/verify`, and `?auth=discord` /
 * `?auth_error=` are where the Discord OAuth callback lands. All three are read and stripped from
 * the address before the session lookup, and the outcome rides back as `notice` for the shell to
 * toast, because a toast fired from here would land before anything is on screen to show it.
 */
import type { Player } from '../net/types-local';
import type { ActivityBridge } from './ActivityBridge';
import { apiPath } from './embedded';

export const TOKEN_STORAGE_KEY = 'sn.token';

export interface Identity {
  token: string | null;
  user: Player;
  /** True when the server never answered and the identity is fabricated (mock only). */
  offline: boolean;
  /** What a sign-in link or the Discord callback did on this load, for the shell to say. */
  notice?: AuthNotice | null;
}

/**
 * The outcome of a sign-in the address carried in. `email_offline` and `email_busy` are separate
 * from `email_expired` because the link is single use only once the server has consumed it: a
 * dropped request, a 5xx or a rate limit (checked before the link is touched) all leave it good,
 * and telling that player to send a new one is wrong.
 */
export type AuthNotice =
  | 'email_ok'
  | 'email_expired'
  | 'email_offline'
  | 'email_busy'
  | 'discord_ok'
  | 'discord_denied'
  | 'discord_error';

/**
 * The sign-in params this page consumes. `login` is a hash fragment (`/play#login=<token>`) so the
 * one-time token never reaches an access log; `auth` / `auth_error` are query params because the
 * server's OAuth callback redirects with them. Everything else (`?join=`, other hash content) stays.
 */
export interface AuthParams {
  login: string | null;
  auth: string | null;
  authError: string | null;
}

const QUERY_PARAMS = ['auth', 'auth_error'] as const;

/**
 * Read the sign-in params and take them off the address (`history.replaceState`), so a reload or
 * a shared screenshot never replays a spent one-time token. Fail-soft: blocked history leaves the
 * address as it was and the params are still returned.
 */
export function consumeAuthParams(loc: Location | undefined = globalThis.location, hist: History | undefined = globalThis.history): AuthParams {
  if (!loc) return { login: null, auth: null, authError: null };
  const params = new URLSearchParams(loc.search);
  const hash = new URLSearchParams(loc.hash.replace(/^#/, ''));
  const out: AuthParams = { login: hash.get('login'), auth: params.get('auth'), authError: params.get('auth_error') };
  if (!QUERY_PARAMS.some((k) => params.has(k)) && !hash.has('login')) return out;
  QUERY_PARAMS.forEach((k) => params.delete(k));
  const query = params.toString();
  let fragment = loc.hash;
  if (hash.has('login')) {
    hash.delete('login');
    const rest = hash.toString();
    fragment = rest ? `#${rest}` : '';
  }
  try {
    hist?.replaceState(hist.state, '', `${loc.pathname}${query ? `?${query}` : ''}${fragment}`);
  } catch {
    /* history blocked; the token is spent server-side either way */
  }
  return out;
}

interface SessionResponse {
  token: string;
  user: { id: string; name?: string; username?: string; displayName?: string; avatar?: string | null; kind?: string };
}

export function readStoredToken(storage: Storage | undefined = safeLocalStorage()): string | null {
  try {
    return storage?.getItem(TOKEN_STORAGE_KEY) ?? null;
  } catch {
    return null;
  }
}

export function writeStoredToken(token: string | null, storage: Storage | undefined = safeLocalStorage()): void {
  try {
    if (token) storage?.setItem(TOKEN_STORAGE_KEY, token);
    else storage?.removeItem(TOKEN_STORAGE_KEY);
  } catch {
    /* storage blocked */
  }
}

function safeLocalStorage(): Storage | undefined {
  try {
    return globalThis.localStorage;
  } catch {
    return undefined;
  }
}

function toPlayer(u: SessionResponse['user'], fallbackKind: Player['kind']): Player {
  return {
    id: u.id,
    name: u.displayName ?? u.name ?? u.username ?? 'Player',
    avatar: u.avatar ?? null,
    kind: u.kind === 'discord' || u.kind === 'email' || u.kind === 'guest' ? u.kind : fallbackKind,
  };
}

export async function establishIdentity(
  bridge: ActivityBridge,
  fetchFn: typeof fetch = fetch,
): Promise<Identity> {
  const stored = readStoredToken();
  if (bridge.kind === 'discord') {
    const { code } = await bridge.authorize();
    const res = await fetchFn(apiPath('/api/auth/discord'), {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ code, guestToken: stored ?? undefined }),
    });
    if (!res.ok) throw new Error(`discord auth failed: HTTP ${res.status}`);
    const body = (await res.json()) as SessionResponse & { access_token: string };
    await bridge.authenticate(body.access_token);
    writeStoredToken(body.token);
    return { token: body.token, user: toPlayer(body.user, 'discord'), offline: false };
  }

  const params = consumeAuthParams();
  try {
    // Discord web callback. The address alone proves nothing (anyone can link to `?auth=discord`),
    // so the stored guest token is left alone: ask with the cookie only, and only a Discord answer
    // counts. Anything else falls through to the ordinary lookup with the guest's own token.
    if (params.auth === 'discord') {
      const viaCookie = await lookupSession(null, fetchFn).catch(() => null);
      if (viaCookie?.user.kind === 'discord') return sessionIdentity(bridge, viaCookie, 'discord_ok');
      return sessionIdentity(bridge, await lookupSession(stored, fetchFn), 'discord_error');
    }
    const { token: bearer, notice } = await redeemAuthParams(params, stored, fetchFn);
    return sessionIdentity(bridge, await lookupSession(bearer, fetchFn), notice);
  } catch (err) {
    const mock = bridge.mockUser();
    if (!mock) throw err;
    return { token: null, user: { id: mock.id, name: mock.username, avatar: null, kind: 'guest' }, offline: true, notice: null };
  }
}

async function lookupSession(bearer: string | null, fetchFn: typeof fetch): Promise<SessionResponse> {
  const res = await fetchFn(apiPath('/api/auth/session'), {
    credentials: 'include',
    headers: bearer ? { authorization: `Bearer ${bearer}` } : {},
  });
  if (!res.ok) throw new Error(`session: HTTP ${res.status}`);
  return (await res.json()) as SessionResponse;
}

/**
 * Re-read the session with the stored token and keep what comes back. For the few moments after
 * boot where the server knows something the client does not (a name that was already picked in
 * another tab). Throws when the server does not answer; the caller keeps what it had.
 */
export async function refreshSession(fetchFn: typeof fetch = fetch): Promise<{ token: string; user: Player }> {
  const body = await lookupSession(readStoredToken(), fetchFn);
  writeStoredToken(body.token);
  return { token: body.token, user: toPlayer(body.user, 'guest') };
}

function sessionIdentity(bridge: ActivityBridge, body: SessionResponse, notice: AuthNotice | null): Identity {
  writeStoredToken(body.token);
  const user = toPlayer(body.user, 'guest');
  const mock = bridge.mockUser();
  // Mock: the server issues the id/token, the mock name labels the seat for screenshots.
  if (mock) user.name = mock.username;
  return { token: body.token, user, offline: false, notice };
}

/**
 * Turn the consumed params into the bearer the session lookup should send, and a notice.
 *
 *  - `login`: verify with the current bearer (the server claims that guest if the link's own guest
 *    is gone), then carry on with the token it issued. On failure the old session stands.
 *  - `auth_error`: nothing changed server-side; say so and keep the guest.
 * (`auth=discord` is handled by the caller: it needs the lookup's answer, not just a bearer.)
 */
async function redeemAuthParams(
  params: AuthParams,
  stored: string | null,
  fetchFn: typeof fetch,
): Promise<{ token: string | null; notice: AuthNotice | null }> {
  if (params.login) {
    try {
      const res = await fetchFn(apiPath('/api/auth/email/verify'), {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json', ...(stored ? { authorization: `Bearer ${stored}` } : {}) },
        body: JSON.stringify({ token: params.login }),
      });
      if (res.ok) {
        const body = (await res.json()) as Partial<SessionResponse>;
        if (typeof body.token === 'string' && body.token) {
          writeStoredToken(body.token);
          return { token: body.token, notice: 'email_ok' };
        }
        return { token: stored, notice: 'email_offline' };
      }
      // 429 is answered before the link is looked at, so it is still good; only a 400 spent it.
      if (res.status === 429) return { token: stored, notice: 'email_busy' };
      return { token: stored, notice: res.status >= 400 && res.status < 500 ? 'email_expired' : 'email_offline' };
    } catch {
      return { token: stored, notice: 'email_offline' };
    }
  }
  if (params.authError) return { token: stored, notice: params.authError === 'denied' ? 'discord_denied' : 'discord_error' };
  return { token: stored, notice: null };
}

/** What each notice says. One place, so the toast and any test read the same words. */
export function authNoticeText(notice: AuthNotice): { text: string; kind: 'success' | 'error' | 'info' } {
  switch (notice) {
    case 'email_ok':
      return { text: 'Signed in with email. Your progress is saved.', kind: 'success' };
    case 'email_expired':
      return { text: 'That sign-in link has expired. Send a new one.', kind: 'error' };
    case 'email_offline':
      return { text: "Couldn't reach the server to sign you in. Open the link again.", kind: 'error' };
    case 'email_busy':
      return { text: 'Too many tries. Wait a minute and open the link again.', kind: 'error' };
    case 'discord_ok':
      return { text: 'Signed in with Discord. Your progress is saved.', kind: 'success' };
    case 'discord_denied':
      return { text: 'Discord sign-in cancelled. You are still playing as a guest.', kind: 'info' };
    case 'discord_error':
      return { text: "Discord sign-in didn't go through. Try again.", kind: 'error' };
  }
}
